/*
 * CRM organization adapter
 * ========================
 *
 * This file is the single boundary for RAW backend/GraphQL organization and
 * address aliases. CustomerCard code consumes canonical properties only.
 *
 * Existing backend address model:
 * - deliveryAddress
 * - invoiceAddress
 * - AddressSettings with displayOrder, label, type, placeholder
 *
 * TODO BACKEND:
 * Replace the group-address source with the future company-address query.
 * CustomerCardDialog should not need to change when that happens.
 */

const firstDefined = (...values) =>
  values.find(value => value !== undefined && value !== null);

const normalizeBoolean = value =>
  value === true || value === 1 || value === "true";

export const CUSTOMER_CARD_ADDRESS_FIELDS = [
  "address1",
  "address2",
  "address3",
  "address4",
  "reference",
  "address5",
  "zip",
  "city",
  "countryCode",
  "email",
  "telephone",
  "contact",
];

const normalizeAddressSettingType = type => {
  const normalized = String(type || "OPTIONAL").toUpperCase();
  return ["DONT_CARE", "HIDDEN", "FIXED", "OPTIONAL", "REQUIRED"].includes(
    normalized
  )
    ? normalized
    : "OPTIONAL";
};

const normalizeCountryCode = source => {
  const raw = firstDefined(
    source?.countryCode,
    source?.country?.code,
    source?.country
  );

  if (typeof raw !== "string") return raw || "";
  return raw.length === 2 ? raw.toUpperCase() : raw;
};

export const normalizeAddressSettings = rawSettings => {
  if (!rawSettings || typeof rawSettings !== "object") return {};

  return Object.entries(rawSettings).reduce((result, [rawKey, rawSetting]) => {
    const key = rawKey;

    if (
      !key ||
      rawKey === "customerId" ||
      !CUSTOMER_CARD_ADDRESS_FIELDS.includes(key)
    ) {
      return result;
    }

    const setting = rawSetting || {};
    result[key] = {
      displayOrder: Number.isFinite(Number(setting.displayOrder))
        ? Number(setting.displayOrder)
        : CUSTOMER_CARD_ADDRESS_FIELDS.indexOf(key),
      label: setting.label || "",
      type: normalizeAddressSettingType(setting.type),
      placeholder: setting.placeholder || "",
    };
    return result;
  }, {});
};

export const getCustomerCardAddressLineOrder = fieldSettings => {
  const settings = fieldSettings || {};
  const fromSettings = Object.entries(settings)
    .filter(([key]) => CUSTOMER_CARD_ADDRESS_FIELDS.includes(key))
    .sort(([, left], [, right]) => {
      const leftOrder = Number(left?.displayOrder);
      const rightOrder = Number(right?.displayOrder);
      return leftOrder !== rightOrder ? leftOrder - rightOrder : 0;
    })
    .map(([key]) => key);

  return [
    ...fromSettings,
    ...CUSTOMER_CARD_ADDRESS_FIELDS.filter(key => !fromSettings.includes(key)),
  ];
};

/*
 * Canonicalize one raw organization address. Raw aliases stop here.
 * The CustomerCard's own model may still fill UI defaults for missing settings,
 * but it never has to know about street/postalCode/postTown/phone/country.code.
 */
export const normalizeOrganizationAddress = (source, rawSettings) => {
  if (!source && !rawSettings) return null;

  const address = source || {};
  const fieldSettings = normalizeAddressSettings(
    rawSettings || address.fieldSettings || {}
  );
  const type = String(address.addressType || address.type || "").toUpperCase();

  const incomingOrder = Array.isArray(address.lineOrder)
    ? address.lineOrder        
        .filter(key => key && CUSTOMER_CARD_ADDRESS_FIELDS.includes(key))
    : [];

  const lineOrder = incomingOrder.length
    ? [
        ...new Set(incomingOrder),
        ...CUSTOMER_CARD_ADDRESS_FIELDS.filter(
          key => !incomingOrder.includes(key)
        ),
      ]
    : getCustomerCardAddressLineOrder(fieldSettings);

  return {
    id: firstDefined(address.id, address.addressId) ?? null,
    name: address.name || "",
    type,
    addressType: type,
    isPrimary: normalizeBoolean(address.isPrimary),
    isDefault: normalizeBoolean(address.isDefault),

    address1: address.address1 || address.street || "",
    address2: address.address2 || "",
    address3: address.address3 || "",
    address4: address.address4 || "",
    address5: address.address5 || "",
    zip: address.zip || address.postalCode || "",
    city: address.city || address.postTown || "",
    countryCode: normalizeCountryCode(address),
    contact: address.contact || "",
    reference: address.reference || "",
    email: address.email || "",
    telephone: address.telephone || address.phone || "",

    fieldSettings,
    lineOrder,
  };
};

const mapBackendAddress = (type, address, rawSettings) =>
  normalizeOrganizationAddress(
    {
      ...(address || {}),
      type,
      addressType: type,
    },
    rawSettings
  );

export const mapGroupAddressResultToCustomerCardAddressSource = result => {
  if (!result) return null;

  const deliveryAddress = mapBackendAddress(
    "DELIVERY",
    result.deliveryAddress,
    result.deliveryAddressSettings
  );
  const billingAddress = mapBackendAddress(
    "INVOICE",
    result.invoiceAddress,
    result.invoiceAddressSettings
  );

  if (!deliveryAddress || !billingAddress) return null;

  return {
    key: `group:${result.groupId}`,
    sourceType: "COMPANY_GROUP",
    sourceId: result.groupId,
    sourceName: result.groupName || "",
    sourceLookup: result.groupLookup || "",
    deliveryAddress,
    billingAddress,
    // The existing resolver returns both explicit address objects.
    deliverySameAsBilling: false,
  };
};

/* Kept for compatibility with any caller outside CustomerCard. */
export const mapGroupAddressResultToOrganizationPatch = result => {
  const addressSource = mapGroupAddressResultToCustomerCardAddressSource(result);
  if (!addressSource) return null;

  return {
    addresses: [addressSource.deliveryAddress, addressSource.billingAddress],
    sourceMeta: {
      sourceType: addressSource.sourceType,
      groupId: addressSource.sourceId,
      groupName: addressSource.sourceName,
      groupLookup: addressSource.sourceLookup,
    },
  };
};

export const normalizeOrganizationOption = source => {
  if (!source) return null;

  const id = firstDefined(source.id, source.companyId, source.organizationId);
  const billingDefaults =
    source.billingDefaults ||
    source.defaultBillingProfile ||
    source.billingProfile ||
    {};

  return {
    id,
    companyId: id,
    name: source.name || source.companyName || source.organizationName || "",
    organizationNumber:
      source.organizationNumber ||
      source.orgNo ||
      source.companyRegistrationNumber ||
      "",
    vatNumber: source.vatNumber || source.vatNo || "",
    customerNumber: source.customerNumber || "",
    logoUrl:
      source.logoUrl ||
      source.platformLogoUrl ||
      source.companyLogoUrl ||
      source.logo ||
      "",
    contactPerson: source.contactPerson || "",
    email: source.email || "",
    telephone: source.telephone || source.phone || "",

    // Keep these source objects intact for compatibility with other adapter consumers.
    // CustomerCard calls normalizeOrganizationAddress() at its boundary.
    addresses: Array.isArray(source.addresses) ? source.addresses : [],
    primaryAddress: source.primaryAddress || source.address || null,

    billingDefaults: {
      paymentMethod: firstDefined(
        billingDefaults.paymentMethod,
        source.paymentMethod
      ),
      paymentTermsDays: firstDefined(
        billingDefaults.paymentTermsDays,
        source.paymentTermsDays
      ),
      distributionMethod: firstDefined(
        billingDefaults.distributionMethod,
        source.distributionMethod
      ),
      invoiceEmail: firstDefined(
        billingDefaults.invoiceEmail,
        source.invoiceEmail
      ),
      currencyCode: firstDefined(
        billingDefaults.currencyCode,
        source.currencyCode
      ),
    },

    visibility: source.visibility || "",
    isPublic:
      source.isPublic === undefined ? undefined : normalizeBoolean(source.isPublic),
    isNew: normalizeBoolean(source.isNew),
    createdAt: source.createdAt || null,
    invoiceDeliveryReady:
      normalizeBoolean(source.invoiceDeliveryReady) ||
      normalizeBoolean(source.receivesInvoicesInSystem) ||
      normalizeBoolean(source.invoiceReceptionEnabled),
    publicCompanyId: source.publicCompanyId || source.publicId || null,
    memberId: source.memberId || null,
    groupId: source.groupId ?? null,
    sourceMeta: source.sourceMeta || null,
  };
};

export const mergeOrganizationSources = (fallback, actual, addressPatch) => {
  const fallbackNormalized = normalizeOrganizationOption(fallback) || {};
  const actualNormalized = normalizeOrganizationOption(actual) || {};

  const merged = {
    ...fallbackNormalized,
    ...actualNormalized,
    billingDefaults: {
      ...(fallbackNormalized.billingDefaults || {}),
      ...(actualNormalized.billingDefaults || {}),
    },
    addresses:
      Array.isArray(actualNormalized.addresses) && actualNormalized.addresses.length
        ? actualNormalized.addresses
        : fallbackNormalized.addresses || [],
  };

  if (addressPatch) {
    return normalizeOrganizationOption({
      ...merged,
      ...addressPatch,
      addresses: addressPatch.addresses || merged.addresses,
      sourceMeta: addressPatch.sourceMeta || merged.sourceMeta,
    });
  }

  return normalizeOrganizationOption(merged);
};

export default {
  CUSTOMER_CARD_ADDRESS_FIELDS,
  normalizeAddressSettings,
  getCustomerCardAddressLineOrder,
  normalizeOrganizationAddress,
  mapGroupAddressResultToOrganizationPatch,
  mapGroupAddressResultToCustomerCardAddressSource,
  normalizeOrganizationOption,
  mergeOrganizationSources,
};
